import { useState } from "react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "./AdminShared";
import OrderShippingActions from "@/components/OrderShippingActions";

function PrintPackBlock({ order }) {
  const [size, setSize] = useState("letter");
  const [includeStory, setIncludeStory] = useState(true);
  const [busy, setBusy] = useState(false);

  const openPdf = async (path, label) => {
    setBusy(true);
    try {
      const { data } = await http.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) toast.error("Popup blocked — allow popups to view PDF.");
      setTimeout(() => URL.revokeObjectURL(url), 90000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not open ${label}`);
    } finally { setBusy(false); }
  };

  const packUrl = `/admin/orders/${order.order_number}/print-pack.pdf?size=${size}&include_story=${includeStory ? "true" : "false"}`;

  return (
    <div className="border border-[#C9A055]/30 bg-[#0e0c08] p-4 space-y-3" data-testid="print-pack-block">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">PRINT &amp; SHIP</div>
          <div className="font-body text-[12px] text-[#B5B0A7] mt-1">One-click PDF pack — certificate {includeStory ? "+ story" : "(certificate only)"} for every physical item in this order.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase">Size</span>
          <select value={size} onChange={(e) => setSize(e.target.value)} className="stc-input !py-1.5 !text-xs" data-testid="print-size-select">
            <option value="letter">Letter (8.5×11)</option>
            <option value="4x6">4×6 Postcard</option>
          </select>
        </div>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={includeStory} onChange={(e) => setIncludeStory(e.target.checked)} className="accent-[#C9A055]" data-testid="include-story-toggle"/>
        <span className="font-ui text-[11px] tracking-[0.22em] text-[#B5B0A7] uppercase">Include story &amp; meaning page</span>
      </label>
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={() => openPdf(packUrl, "print pack")} className="btn-gold text-xs" data-testid="print-pack-btn">
          {busy ? "Loading…" : "Open Print Pack"}
        </button>
        <button disabled={busy} onClick={() => openPdf(`/admin/certificate-preview.pdf?size=${size}`, "preview")} className="btn-outline text-xs" data-testid="print-cert-preview-btn">
          Preview Sample Cert
        </button>
      </div>
      <p className="font-body text-[11px] text-[#6a6660]">
        Tip: Letter prints full-page for framing. 4×6 prints as a ship-along postcard. Uncheck "Include story" if you only want certificates.
      </p>
    </div>
  );
}

export default function OrderModal({ order, onClose, onSaved }) {
  const [status, setStatus] = useState(order.status || "pending");
  const [paymentStatus, setPaymentStatus] = useState(order.payment_status || "pending");
  const [tracking, setTracking] = useState(order.tracking || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await http.put(`/orders/${order.id}`, { status, payment_status: paymentStatus, tracking });
      toast.success("Order updated.");
      onSaved();
    } catch { toast.error("Update failed."); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Order ${order.order_number}`} onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Customer</div><div className="text-[#F4F1EA]">{order.name || order.email}</div><div className="text-[#B5B0A7]">{order.email}</div></div>
          <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Placed</div><div className="text-[#F4F1EA]">{new Date(order.created_at).toLocaleString()}</div><div className="text-[#B5B0A7] capitalize">{order.payment_method}</div></div>
        </div>
        {order.shipping_address && Object.values(order.shipping_address).some(Boolean) && (
          <div className="border border-white/10 p-3">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] mb-1">Shipping Address</div>
            <div className="text-[#F4F1EA]">{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</div>
            <div className="text-[#B5B0A7]">{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</div>
            <div className="text-[#B5B0A7]">{order.shipping_address.country}</div>
          </div>
        )}
        <div className="border border-white/10 p-3 space-y-2">
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] mb-1">Items</div>
          {(order.items || []).map((it, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <img src={resolveImage(it.main_image)} alt="" className="w-14 h-14 object-cover"/>
              <div className="flex-1">
                <div className="text-[#F4F1EA]">{it.title}</div>
                <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{it.variant_label} × {it.quantity}</div>
                {it.personal_text && <div className="font-body italic text-[12px] text-[#B5B0A7] mt-0.5">"{it.personal_text}"</div>}
              </div>
              <div className="text-[#F4F1EA]">${it.line_total.toFixed(2)}</div>
            </div>
          ))}
          <div className="border-t border-white/10 pt-2 mt-2 text-[#B5B0A7] space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>${order.shipping.toFixed(2)}</span></div>
            {order.credit_applied > 0 && <div className="flex justify-between text-[#C9A055]"><span>Credit applied</span><span>-${order.credit_applied.toFixed(2)}</span></div>}
            <div className="flex justify-between text-[#F4F1EA] font-semibold pt-1 border-t border-white/5"><span>Total</span><span className="text-[#C9A055]">${order.total.toFixed(2)}</span></div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Payment Status</div>
            <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)} className="stc-input">
              {["pending","manual_pending","paid","failed","refunded"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Order Status</div>
            <select value={status} onChange={e => setStatus(e.target.value)} className="stc-input">
              {["pending","processing","shipped","completed","canceled"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Tracking Number</div>
            <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Optional" className="stc-input"/>
          </div>
        </div>

        <OrderShippingActions order={order} onLabelPurchased={() => { onSaved && onSaved(); }} />
        <PrintPackBlock order={order} />

        <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
          <button onClick={onClose} className="btn-outline">Close</button>
          <button onClick={save} disabled={saving} className="btn-gold" data-testid="order-save-btn">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </Modal>
  );
}
